import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isNil } from 'ramda'
import { navigate } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { selectCurrentUserId } from 'src/features/account/duck/selectors'
import { Title } from 'src/features/stocks/components/typography'
import Timeline from 'src/features/stocks/components/OfferDetails/Timeline'
import Shade from 'src/components/Shade'
import { OFFER_TYPES } from 'src/features/offers/duck/consts'
import BidOffer from 'src/features/stocks/components/BidOffer'
import Contact from 'src/features/stocks/components/OfferDetails/Contact'
import {
  AuctionPriceOfferDetails,
  ButtonsForCustomer,
  ButtonsForOwner,
  ContainerInfo,
  ContainerSemiTrailerInfo,
  CreatedDateOfferDetails,
  CustomsClearance,
  DrawerTitle,
  GeneralOfferFlagsInfo,
  MiddleContainer,
  Notes,
  OfferBelongsToCurrentUserInfo,
  OfferDebtCollectionInfo,
  OfferHasTrackingInfo,
  Price,
  OfferRemovedInfo
} from 'src/features/stocks/components/OfferDetails/atoms'
import { STOCKS_TYPES } from 'src/features/stocks/ducks/consts'
import routes from 'src/utils/routes'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'
import { addOfferToHistory } from './offerHistory'
import {
  OFFER_FAVORITES_CHANGED_EVENT,
  useOfferFavorites
} from './offerFavorites'
import OfferHistoryDrawer from './OfferHistoryDrawer'
import { focusOfferOnMap } from '../map/offerMapFocus'

const OfferDetails = ({
  className,
  setDrawerState,
  onDrawerClose,
  offerDetails,
  timelineData,
  checkIfTheOfferBelongsToTheCurrentCompany,
  checkIfTheOfferBelongsToTheCurrentUser,
  updateOffer,
  reserveOffer,
  stock,
  refreshOffer,
  setConversationOpened,
  bidOffer,
  onHistoryOfferSelect
}) => {
  const currentUserId = useSelector(selectCurrentUserId)
  const closeDrawer = onDrawerClose || setDrawerState
  const offerRemoved = !isNil(offerDetails.deletedAt)
  const isAuction = offerDetails.type === OFFER_TYPES.AUCTION
  const hasTracking =
    offerDetails.doesRequireTracking || offerDetails.doesAllowTracking
  const hasCustomsClearance = offerDetails.customsClearanceLocation.id
  const hasContainer = offerDetails.container && offerDetails.container.name
  const hasContainerSemiTrailer =
    offerDetails.containerSemiTrailer && offerDetails.containerSemiTrailer.name
  const hasNotes = offerDetails.notes
  const hasPrice =
    offerDetails.price && offerDetails.price.float > 0 && !isAuction
  const offerBelongsToCurrentUserCompany = checkIfTheOfferBelongsToTheCurrentCompany(
    offerDetails.issuer.companyId
  )
  const offerBelongsToCurrentUser = checkIfTheOfferBelongsToTheCurrentUser(
    offerDetails.handler.id
  )
  const [bidDialog, setBidDialog] = useState(false)
  const [offerHistoryState, setOfferHistoryState] = useState({
    userId: null,
    history: []
  })
  const offerHistory =
    offerHistoryState.userId === currentUserId
      ? offerHistoryState.history
      : []
  const [savedOffersView, setSavedOffersView] = useState(null)
  const [animateFavoritesButton, setAnimateFavoritesButton] = useState(false)
  const favoritesAnimationTimeoutRef = useRef(null)
  const {
    favorites,
    isFavorite,
    removeFavorite,
    toggleFavorite
  } = useOfferFavorites()
  const favoritesCountRef = useRef(favorites.length)
  favoritesCountRef.current = favorites.length
  const hidePrice =
    !offerBelongsToCurrentUserCompany &&
    !offerDetails.isPriceVisible &&
    offerDetails.reservationsCount === 0

  useEffect(() => {
    if (
      !offerDetails.id ||
      currentUserId === null ||
      currentUserId === undefined
    ) {
      setOfferHistoryState({ userId: currentUserId, history: [] })
      return
    }
    setOfferHistoryState({
      userId: currentUserId,
      history: addOfferToHistory(offerDetails, currentUserId)
    })
    setSavedOffersView(null)
    focusOfferOnMap(offerDetails)
  }, [
    currentUserId,
    offerDetails.id,
    offerDetails.offerType,
    offerDetails.referenceNumber
  ])

  useEffect(
    () => () => {
      if (favoritesAnimationTimeoutRef.current) {
        window.clearTimeout(favoritesAnimationTimeoutRef.current)
      }
    },
    []
  )

  const triggerFavoritesButtonAnimation = useCallback(() => {
    setAnimateFavoritesButton(true)
    if (favoritesAnimationTimeoutRef.current) {
      window.clearTimeout(favoritesAnimationTimeoutRef.current)
    }
    favoritesAnimationTimeoutRef.current = window.setTimeout(
      () => setAnimateFavoritesButton(false),
      450
    )
  }, [])

  useEffect(() => {
    const handleFavoritesChanged = event => {
      if (
        favoritesCountRef.current === 0 &&
        event &&
        event.detail &&
        event.detail.userId === currentUserId &&
        Array.isArray(event.detail.favorites) &&
        event.detail.favorites.length > 0
      ) {
        triggerFavoritesButtonAnimation()
      }
    }

    window.addEventListener(
      OFFER_FAVORITES_CHANGED_EVENT,
      handleFavoritesChanged
    )
    return () =>
      window.removeEventListener(
        OFFER_FAVORITES_CHANGED_EVENT,
        handleFavoritesChanged
      )
  }, [currentUserId, triggerFavoritesButtonAnimation])

  const currentOfferIsFavorite = isFavorite(
    offerDetails.offerType,
    offerDetails.id
  )
  const handleFavoriteToggle = () => {
    if (!currentOfferIsFavorite && favorites.length === 0) {
      triggerFavoritesButtonAnimation()
    }
    toggleFavorite(offerDetails)
  }

  const handleHistoryOfferSelect = historyOffer => {
    setSavedOffersView(null)
    onHistoryOfferSelect(historyOffer)
  }

  if (savedOffersView) {
    return (
      <div className={className} data-history-view>
        <OfferHistoryDrawer
          history={savedOffersView === 'favorites' ? favorites : offerHistory}
          mode={savedOffersView}
          offerDetails={offerDetails}
          onBack={() => setSavedOffersView(null)}
          onClose={() => closeDrawer(false)}
          onFavoriteRemove={removeFavorite}
          onOfferSelect={handleHistoryOfferSelect}
        />
      </div>
    )
  }

  const changeHandler = newHandlerId => {
    updateOffer(offerDetails.id, { handler_id: newHandlerId })
  }

  const navigateToMyOffers = () => {
    const link = {
      [STOCKS_TYPES.GENERAL]: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_GENERAL}`,
      [STOCKS_TYPES.POST_IMPORT]: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_POST_IMPORT_OFFERS}`,
      [STOCKS_TYPES.CARGO_EXPORT]: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_EXPORT_OFFERS}`,
      [STOCKS_TYPES.CARGO_IMPORT]: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_IMPORT_OFFERS}`,
      [STOCKS_TYPES.FREE_CARRIERS]: `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_FREE_CARRIERS_OFFERS}`
    }[offerDetails.offerType]
    const options = { state: { openOfferId: offerDetails.id } }
    navigate(link, options)
  }
  if (offerRemoved) {
    return (
      <div className={className}>
        <DrawerTitle
          animateFavoritesButton={animateFavoritesButton}
          offerDetails={offerDetails}
          favorites={favorites}
          history={offerHistory}
          isFavorite={currentOfferIsFavorite}
          onFavoriteToggle={handleFavoriteToggle}
          onFavoritesOpen={() => setSavedOffersView('favorites')}
          onHistoryOfferSelect={onHistoryOfferSelect}
          onHistoryOpen={() => setSavedOffersView('history')}
          setDrawerState={closeDrawer}
        />
        <OfferRemovedInfo />
      </div>
    )
  }

  return (
    <div className={className}>
      {/* {JSON.stringify(offerDetails)} */}
      {stock?.isLoading('details') && <Shade />}
      <DrawerTitle
        animateFavoritesButton={animateFavoritesButton}
        offerDetails={offerDetails}
        favorites={favorites}
        history={offerHistory}
        isFavorite={currentOfferIsFavorite}
        onFavoriteToggle={handleFavoriteToggle}
        onFavoritesOpen={() => setSavedOffersView('favorites')}
        onHistoryOfferSelect={onHistoryOfferSelect}
        onHistoryOpen={() => setSavedOffersView('history')}
        setDrawerState={closeDrawer}
      />
      {offerDetails.id && (
        <MiddleContainer>
          {offerBelongsToCurrentUserCompany && (
            <OfferBelongsToCurrentUserInfo />
          )}
          {hasTracking && <OfferHasTrackingInfo />}
          <OfferDebtCollectionInfo company={offerDetails.issuer?.company} />
          {isAuction && (
            <AuctionPriceOfferDetails
              offerDetails={offerDetails}
              hidePrice={hidePrice}
            />
          )}
          {timelineData && <Timeline data={timelineData} />}
          {hasCustomsClearance && (
            <CustomsClearance offerDetails={offerDetails} />
          )}
          <GeneralOfferFlagsInfo offerDetails={offerDetails} />
          {hasContainer && <ContainerInfo offerDetails={offerDetails} />}
          {hasContainerSemiTrailer && (
            <ContainerSemiTrailerInfo offerDetails={offerDetails} />
          )}
          {hasNotes && <Notes offerDetails={offerDetails} />}
          {hasPrice && <Price offerDetails={offerDetails} />}
          <Contact
            issuer={offerDetails.issuer}
            stockType={stock.offerDetails.offerType}
            handler={offerDetails.handler}
            changeHandler={changeHandler}
            offerBelongsToCurrentUserCompany={offerBelongsToCurrentUserCompany}
            offerBelongsToCurrentUser={offerBelongsToCurrentUser}
            isUpdatable={offerDetails.isUpdatable}
            handleConversationStart={() => {
              setConversationOpened({
                receiverId: offerDetails.handlerId,
                contextId: offerDetails.id,
                contextType: offerDetails.offerContextType,
                conversationOpened: true
              })
            }}
          />
          <CreatedDateOfferDetails offerDetails={offerDetails} />
          {/* {offerDetails.isPrivate && (
            <PrivateOfferCountdown offerDetails={offerDetails} />
          )} */}
        </MiddleContainer>
      )}
      {!offerBelongsToCurrentUserCompany && !offerRemoved && (
        <ButtonsForCustomer
          offerDetails={offerDetails}
          setBidDialog={setBidDialog}
          stock={stock}
          reserveOffer={reserveOffer}
          setConversationOpened={setConversationOpened}
        />
      )}
      {offerBelongsToCurrentUserCompany && !offerRemoved && (
        <ButtonsForOwner
          offerDetails={offerDetails}
          stock={stock}
          refreshOffer={refreshOffer}
          navigateToMyOffers={navigateToMyOffers}
        />
      )}
      <BidOffer
        open={bidDialog}
        setOpen={setBidDialog}
        offer={offerDetails}
        onClick={price => bidOffer({ id: offerDetails.id, price })}
      />
    </div>
  )
}

export default styled(OfferDetails)`
  position: absolute;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 1.6rem 0 80px 0;
  &[data-history-view] {
    padding-bottom: 0;
  }
  ${Title} {
    padding-bottom: 4px;
  }
`
